import type { AppDefinition, WorkspaceRole, ActiveAppEntry } from '@clarix/shared-types';
import { Boxes } from 'lucide-react';
import { APP_REGISTRY } from './apps';
/**
 * Fallback icon for a backend-registered app that the shell has no static
 * definition for. Lets a freshly-registered app still render in the switcher /
 * launcher without a frontend change (see synthesizeApp).
 */
const FALLBACK_APP_ICON = Boxes;

interface MeLike {
  active_workspace_apps?: Record<string, ActiveAppEntry>;
  active_workspace_slug?: string | null;
  workspaces?: { slug: string; role: WorkspaceRole; admin_permissions?: string[] }[];
}

interface AccessOptions {
  /** JWT-claim role, used as a fallback while /me is still loading. */
  role?: WorkspaceRole;
  /** Slug of the workspace being viewed (URL subdomain), if known. */
  activeSlug?: string;
}

/**
 * The registry apps the current user can open in the active workspace, with
 * their live name/description overlaid from the backend App registry.
 *
 * Visibility is driven entirely by the backend registry (via /me's
 * `active_workspace_apps` map) — no hardcoded per-app gate:
 *   - workspace   → workspace OWNER/ADMIN, or a MEMBER holding at least one
 *                   admin capability (it is the workspace admin console, not a
 *                   catalog app, so it has no registry row)
 *   - other apps  → the workspace has the app enabled
 *                   (active_workspace_apps[CODE].status === 'ACTIVE') AND the
 *                   app is generally available in the registry
 *                   (registry_status === 'ACTIVE'). BETA / COMING_SOON /
 *                   DEPRECATED apps stay hidden until their registry status is
 *                   changed in the platform-admin console.
 *
 * A backend-registered app with NO static definition here (e.g. a brand-new
 * "SUPPORT" app registered in the platform-admin console) still shows up: a display-only
 * entry is synthesized from the /me metadata with a fallback icon. Enabling an
 * app in the admin therefore surfaces it in the switcher with no frontend
 * change. (It can be *shown* and *named*; it can only be fully *opened* once a
 * Module-Federation remote for it is added to APP_REGISTRY.)
 *
 * App codes in `active_workspace_apps` are the uppercased slug (e.g. 'PROJECTS').
 * The live role from /me's workspaces list is preferred over the JWT claim so a
 * freshly promoted ADMIN sees admin without re-login.
 */
export function getAccessibleApps(
  me: MeLike | null | undefined,
  opts: AccessOptions = {},
): AppDefinition[] {
  const access = me?.active_workspace_apps ?? {};
  const activeSlug = opts.activeSlug ?? me?.active_workspace_slug ?? undefined;
  const membership = me?.workspaces?.find((w) => w.slug === activeSlug);
  const liveRole = membership?.role ?? opts.role;
  // A MEMBER whose Member role has been granted an admin capability reaches the
  // console for exactly that capability, so the card has to appear for them too.
  // Capabilities come from live /me — the JWT carries only the role.
  const canSeeAdmin =
    liveRole === 'OWNER' ||
    liveRole === 'ADMIN' ||
    (membership?.admin_permissions?.length ?? 0) > 0;

  // 1. Apps the shell has a static definition for, in registry order. The
  //    admin app is role-gated; the rest follow backend enablement.
  const known = APP_REGISTRY.filter((app) => {
    if (app.id === 'workspace') return canSeeAdmin;
    return isAppLaunchable(access[app.slug.toUpperCase()]);
  }).map((app) => withRegistryMeta(app, access[app.slug.toUpperCase()]));

  // 2. Backend-registry apps with no static definition — synthesize a display
  //    entry so a newly-registered + enabled app appears automatically.
  const knownCodes = new Set(APP_REGISTRY.map((a) => a.slug.toUpperCase()));
  const synthesized = Object.entries(access)
    .filter(([code, entry]) => !knownCodes.has(code) && isAppLaunchable(entry))
    .map(([code, entry]) => synthesizeApp(code, entry));

  return [...known, ...synthesized];
}

/**
 * An app is launchable when the workspace has it ACTIVE and the registry marks
 * it generally available. A missing `registry_status` (older backend that
 * predates the enriched /me map) is treated as available so an enabled app is
 * never wrongly hidden during a transitional deploy.
 */
function isAppLaunchable(entry: ActiveAppEntry | undefined): boolean {
  if (entry?.status !== 'ACTIVE') return false;
  return entry.registry_status === undefined || entry.registry_status === 'ACTIVE';
}

/** A registry string that is present and not blank, else undefined — so an
 *  empty/whitespace value from the backend falls back to the static text
 *  instead of rendering an empty name/blurb (`??` alone keeps `''`). */
function meaningful(value: string | null | undefined): string | undefined {
  return value && value.trim() ? value : undefined;
}

/** Overlay the live registry name/description/favicon onto the static definition,
 *  so the AppSwitcher label and the browser tab (DocumentMeta) render from the
 *  backend App registry. A blank/absent registry favicon keeps the bundled one. */
function withRegistryMeta(app: AppDefinition, entry: ActiveAppEntry | undefined): AppDefinition {
  if (!entry) return app;
  const favicon = meaningful(entry.favicon) ?? app.favicon;
  // `brandColor` tints chrome that sits next to the app's mark (the launcher
  // card's hover), so it has to describe the favicon we ACTUALLY render — it is
  // the one field where the registry does not automatically win. The registered
  // brand_color routinely disagrees with the shipped art: SUPPORT is registered
  // #dc2626 (red) but ships a violet mark, CRM #0891b2 (cyan) against an orange
  // one. Trust the registry's colour only when the registry also replaced the
  // icon — then it describes that new mark rather than contradicting ours.
  const registryColor = meaningful(entry.brand_color);
  const usesRegistryFavicon = favicon !== app.favicon;
  return {
    ...app,
    name: meaningful(entry.name) ?? app.name,
    description: meaningful(entry.description) ?? app.description,
    favicon,
    brandColor: usesRegistryFavicon
      ? (registryColor ?? app.brandColor)
      : (app.brandColor ?? registryColor),
  };
}

/**
 * Build a display-only AppDefinition for a backend app the shell has no static
 * definition (icon/favicon/remote) for. It renders in the switcher/launcher
 * with a fallback icon; `favicon: ''` signals "no bundled image" so the
 * switcher falls back to the icon component. The loader rejects because there
 * is no Module-Federation remote — routes for such apps aren't registered, so
 * it is never invoked from routing.
 *
 * `displayOnly: true` tells the switcher/launcher NOT to navigate to it: since
 * it has no route, a click would fall through the router catch-all to the
 * default app (wrong app) or, if it is the user's only accessible app, into a
 * redirect loop with the launcher's single-app auto-enter.
 */
function synthesizeApp(code: string, entry: ActiveAppEntry): AppDefinition {
  const slug = code.toLowerCase();
  return {
    id: slug,
    slug,
    name: meaningful(entry.name) ?? code,
    description: meaningful(entry.description),
    brandColor: meaningful(entry.brand_color),
    icon: FALLBACK_APP_ICON,
    // Registry favicon if the backend supplied one, else '' → switcher uses the icon.
    favicon: meaningful(entry.favicon) ?? '',
    permissions: [],
    displayOnly: true,
    loader: () => Promise.reject(new Error(`No remote registered for app "${code}"`)),
  };
}
